/**
 * YetkiliProfil.jsx — "Firma Yetkilisi" (profil grubu) için ortak parçalar.
 * Backlog #106 (backend #105).
 *
 * İki sayfada da aynı: denetmen tarafı `FirmaProfilIncelemePage.jsx` ve firma portalı
 * `FirmaKartPage.jsx`. İkisinin kart çerçevesi farklı (header / ribbon) ama içi aynı olduğu
 * için burada yalnızca İÇ kısım var; çerçeveyi sayfa kendi diliyle çizer.
 *
 *  - YetkiliProfilKarti      : fotoğraf + alanlar + biyografi (yalnızca profil grubu,
 *                              bkz. yetkiliGruplari.PROFIL_GRUP_KODU; İSG rolleri eski kartta)
 *  - YetkiliProfilAlanlari   : ekleme/düzenleme modalındaki ek alanlar
 *  - yetkiliKaydet           : kaydet + fotoğraf yükle/kaldır akışı (iki sayfa da kullanır)
 *
 * Backend sözleşmesi util/knowledgebase/firma-yetkilileri.md'de. Akılda tutulacaklar:
 *  - `dogumTarihi` ve `kullanici*` alanları GET /profile-page'de başka firmaların girişi
 *    için null gelir; denetmen ve firmanın kendisi hepsini görür.
 *  - Fotoğraf satır oluştuktan SONRA ayrı uçtan yüklenir (POST /yetkililer/{id}/fotograf),
 *    yani "yeni yetkili + fotoğraf" iki istektir.
 *  - PUT gerçek kısmi güncelleme: gönderilen anahtar değişir, `""`/null olan temizlenir,
 *    `kullaniciId: null` bağlantıyı koparır.
 */
import React, { useEffect, useState } from "react";
import { Row, Col, Badge, Input, Label, Button } from "reactstrap";
import { UserRound } from "lucide-react";
import { InfoField } from "./FirmaBilgiKartlari";
import TarihGirdisi from "../Common/TarihGirdisi";
import { firmaFileSrc } from "../../services/firmaMediaService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

// ─── Sınırlar (backend: FirmaService DEPARTMAN_MAX / LINKEDIN_MAX / BIYOGRAFI_MAX, fotoğraf 5 MB) ──
export const DEPARTMAN_MAX = 255;
export const LINKEDIN_MAX = 500;
export const BIYOGRAFI_MAX = 2000;
export const YETKILI_FOTO_MAX_BYTES = 5 * 1024 * 1024;
export const YETKILI_FOTO_UZANTILAR = ["png", "jpg", "jpeg", "webp"]; // SVG kabul EDİLMİYOR

/** Modal formunun boş hali — iki sayfa da buradan alıyor ki alanlar ayrışmasın. */
export const EMPTY_YETKILI_FORM = {
  adSoyad: "", unvan: "", telefon: "", email: "",
  departman: "", dogumTarihi: "", linkedin: "", biyografi: "", kullaniciId: "",
};

/** Modaldaki fotoğraf durumu: seçilen dosya, önizleme, "mevcut olanı kaldır" bayrağı. */
export const EMPTY_YETKILI_FOTO = { file: null, onizleme: null, sil: false, mevcutUrl: null };

/** Kayıttan modal formu (düzenleme) — tüm alanlar string, input'lar kontrollü kalsın diye. */
export function yetkiliFormundan(kisi = {}) {
  return {
    adSoyad: kisi.adSoyad || "", unvan: kisi.unvan || "",
    telefon: kisi.telefon || "", email: kisi.email || "",
    departman: kisi.departman || "", dogumTarihi: kisi.dogumTarihi || "",
    linkedin: kisi.linkedin || "", biyografi: kisi.biyografi || "",
    kullaniciId: kisi.kullaniciId != null ? String(kisi.kullaniciId) : "",
  };
}

/**
 * POST/PUT gövdesi. Profil alanları yalnızca profil grubunun rolünde gönderilir: İSG
 * rollerinin formunda bu alanlar yok, ve PUT'ta hiç göndermemek mevcut değeri korur.
 * Düzenlemede form mevcut değerlerle dolduruldu, yani boşaltılan alan bilerek temizlenir.
 */
export function yetkiliPayload(rol, form, profilli) {
  const payload = {
    rol, adSoyad: form.adSoyad.trim(), unvan: form.unvan, telefon: form.telefon, email: form.email,
  };
  if (profilli) {
    payload.departman = form.departman;
    payload.dogumTarihi = form.dogumTarihi; // "" backend'de temizleme demek, yyyy-MM-dd = input[type=date]
    payload.linkedin = form.linkedin;
    payload.biyografi = form.biyografi;
    payload.kullaniciId = form.kullaniciId === "" ? null : Number(form.kullaniciId);
  }
  return payload;
}

// ─── Biçimleme ────────────────────────────────────────────────────────────────

// `new Date()` DEĞİL: "1980-01-01" UTC gece yarısı olarak okunur, negatif saat diliminde bir
// gün geri kayar. Dize zaten ISO, parçalamak yeterli.
export function formatTarih(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}.${m[2]}.${m[1]}` : null;
}

export function formatTarihSaat(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso || "");
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}` : formatTarih(iso);
}

/**
 * Serbest metin LinkedIn alanından güvenli bir href. Kullanıcı girdisi: `javascript:` gibi
 * şemalar bağlantıya dönmez (null → düz metin). Şemasız "linkedin.com/in/x" https alır.
 */
export function linkedinHref(raw) {
  const s = (raw || "").trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[a-z][a-z0-9+.-]*:/i.test(s)) return null;
  return `https://${s.replace(/^\/+/, "")}`;
}

/** Seçilen dosya için kullanıcıya gösterilecek hata, uygunsa null. */
export function yetkiliFotoHatasi(file) {
  const uzanti = (file.name.split(".").pop() || "").toLowerCase();
  if (!YETKILI_FOTO_UZANTILAR.includes(uzanti)) return i18n.t("components:yetkiliProfil.yalnizcaPngJpgVeyaWebp");
  if (file.size > YETKILI_FOTO_MAX_BYTES) return i18n.t("components:yetkiliProfil.fotografEnFazla5Mb");
  return null;
}

// ─── Görünüm ──────────────────────────────────────────────────────────────────

/** Kare fotoğraf; yoksa ya da yüklenemezse nötr bir ikon. */
export function YetkiliFoto({ src, size = 150 }) {
  const [bozuk, setBozuk] = useState(false);
  const [oncekiSrc, setOncekiSrc] = useState(src);
  // src değişince (yeni yükleme) önceki hata bayrağını sıfırla — render sırasında, effect'siz.
  if (src !== oncekiSrc) {
    setOncekiSrc(src);
    setBozuk(false);
  }
  return (
    <div
      className="bg-white border rounded-2 p-1 d-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: size, height: size }}
    >
      {src && !bozuk ? (
        <img
          src={src} alt="" onError={() => setBozuk(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <UserRound size={Math.round(size / 3)} className="text-muted" />
      )}
    </div>
  );
}

/**
 * Profil grubundaki kişinin kartı. `actions` (düzenle/sil düğmeleri) sağda durur; yetkisi
 * olmayan taraf (ör. İSG'yi yönetemeyen firma) vermez.
 */
export function YetkiliProfilKarti({ kisi, actions = null }) {
  const { t } = useTranslation("components");
  const hesapVar = kisi.kullaniciId != null;
  const href = linkedinHref(kisi.linkedin);
  const xl = hesapVar ? 3 : 4;
  return (
    <div>
      <div className="d-flex align-items-start gap-4 flex-wrap">
        <YetkiliFoto src={firmaFileSrc(kisi.fotografUrl)} />
        <div className="flex-grow-1" style={{ flexBasis: 320, minWidth: 0 }}>
          <Row className="text-break">
            <Col md={6} xl={xl}>
              <InfoField label={t("yetkiliProfil.adSoyad")} value={kisi.adSoyad} />
              <InfoField label={t("yetkiliProfil.unvan")} value={kisi.unvan || null} />
              <InfoField label={t("yetkiliProfil.departman")} value={kisi.departman || null} />
              <InfoField label={t("yetkiliProfil.dogumTarihi")} value={formatTarih(kisi.dogumTarihi)} />
            </Col>
            <Col md={6} xl={xl}>
              <InfoField label={t("yetkiliProfil.email")} value={kisi.email || null} />
              <InfoField label={t("yetkiliProfil.telefon")} value={kisi.telefon || null} />
              <div className="mb-2 small">
                <span className="text-muted">{t("yetkiliProfil.linkedIn")}</span>{" "}
                {href ? (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="text-danger fw-semibold">
                    {kisi.linkedin}
                  </a>
                ) : (
                  <span className="fw-semibold">{kisi.linkedin || "—"}</span>
                )}
              </div>
            </Col>
            {hesapVar && (
              <>
                <Col md={6} xl={xl}>
                  <InfoField label={t("yetkiliProfil.kullaniciAdi")} value={kisi.kullaniciAdi} />
                  <InfoField label={t("yetkiliProfil.kullaniciRol")} value={kisi.kullaniciRol} />
                  <div className="mb-2 small">
                    <span className="text-muted">{t("yetkiliProfil.durum")}</span>{" "}
                    <Badge color={kisi.kullaniciAktif ? "success" : "danger"}>
                      {kisi.kullaniciAktif ? t("yetkiliProfil.aktif") : t("yetkiliProfil.pasif")}
                    </Badge>
                  </div>
                </Col>
                <Col md={6} xl={xl}>
                  <InfoField label={t("yetkiliProfil.kayitTarihi")} value={formatTarih(kisi.kullaniciKayitTarihi)} />
                  <InfoField
                    label={t("yetkiliProfil.sonGiris")}
                    value={formatTarihSaat(kisi.kullaniciSonGiris) || t("yetkiliProfil.henuzGirisYok")}
                  />
                </Col>
              </>
            )}
          </Row>
        </div>
        {actions && <div className="d-flex flex-column gap-1 flex-shrink-0">{actions}</div>}
      </div>
      {kisi.biyografi && (
        <div className="mt-3">
          <div className="fw-semibold text-muted mb-1">{t("yetkiliProfil.biyografi")}</div>
          <p className="small mb-0" style={{ whiteSpace: "pre-line" }}>{kisi.biyografi}</p>
        </div>
      )}
    </div>
  );
}

// ─── Form ─────────────────────────────────────────────────────────────────────

/**
 * Modalın profil grubuna özel alanları (rol, ad soyad, ünvan, telefon, email sayfanın kendi
 * modalında kalır). `kullanicilar` firmanın portal hesapları: `{ id, adSoyad, email }` —
 * backend yalnızca firmanın KENDİ hesaplarını kabul eder (user_to_firma).
 */
export function YetkiliProfilAlanlari({ form, onFormChange, foto, onFotoChange, kullanicilar = [] }) {
  const { t } = useTranslation("components");
  const [fotoHata, setFotoHata] = useState(null);
  const set = (key) => (e) => onFormChange({ ...form, [key]: e.target.value });

  // Önizleme nesne URL'i seçim anında üretiliyor (effect'te setState yok); bu effect yalnızca
  // bırakılan URL'i serbest bırakıyor.
  useEffect(() => () => { if (foto.onizleme) URL.revokeObjectURL(foto.onizleme); }, [foto.onizleme]);

  const fotoSrc = foto.onizleme ?? (foto.sil ? null : firmaFileSrc(foto.mevcutUrl));

  function dosyaSec(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const hata = yetkiliFotoHatasi(file);
    setFotoHata(hata);
    e.target.value = "";
    if (hata) return;
    onFotoChange({ ...foto, file, onizleme: URL.createObjectURL(file), sil: false });
  }

  function fotoKaldir() {
    setFotoHata(null);
    onFotoChange({ ...foto, file: null, onizleme: null, sil: !!foto.mevcutUrl });
  }

  const bugun = new Date().toISOString().slice(0, 10);

  return (
    <>
      <Row className="mt-2">
        <Col md={6}>
          <Label className="small">{t("yetkiliProfil.departman")}</Label>
          <Input bsSize="sm" value={form.departman} maxLength={DEPARTMAN_MAX} onChange={set("departman")} />
        </Col>
        <Col md={6}>
          <Label className="small">{t("yetkiliProfil.dogumTarihi")}</Label>
          <TarihGirdisi value={form.dogumTarihi} max={bugun} onChange={(v) => onFormChange({ ...form, dogumTarihi: v })} />
        </Col>
      </Row>
      <div className="mt-2">
        <Label className="small">{t("yetkiliProfil.linkedIn2")}</Label>
        <Input
          bsSize="sm" value={form.linkedin} maxLength={LINKEDIN_MAX} placeholder="linkedin.com/in/…"
          onChange={set("linkedin")}
        />
      </div>
      <div className="mt-2">
        <Label className="small">{t("yetkiliProfil.bagliKullaniciHesabi")}</Label>
        <Input type="select" bsSize="sm" value={form.kullaniciId} onChange={set("kullaniciId")}>
          <option value="">{t("yetkiliProfil.bagliDegil")}</option>
          {kullanicilar.map((u) => (
            <option key={u.id} value={String(u.id)}>
              {u.adSoyad || t("yetkiliProfil.kullanici", { id: u.id })}{u.email ? ` — ${u.email}` : ""}
            </option>
          ))}
        </Input>
      </div>
      <div className="mt-2">
        <Label className="small">{t("yetkiliProfil.biyografi")}</Label>
        <Input
          type="textarea" rows={4} bsSize="sm" value={form.biyografi} maxLength={BIYOGRAFI_MAX}
          onChange={set("biyografi")}
        />
        <div className="text-muted small text-end">{form.biyografi.length}/{BIYOGRAFI_MAX}</div>
      </div>
      <div className="mt-2">
        <Label className="small">{t("yetkiliProfil.fotograf")}</Label>
        <div className="d-flex align-items-center gap-3">
          <YetkiliFoto src={fotoSrc} size={72} />
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <Input
              key={foto.file ? "dolu" : "bos"}
              type="file" bsSize="sm" accept="image/png,image/jpeg,image/webp" onChange={dosyaSec}
            />
            {(foto.file || (foto.mevcutUrl && !foto.sil)) && (
              <Button color="link" size="sm" className="text-danger p-0 mt-1" onClick={fotoKaldir}>
                {t("yetkiliProfil.fotografiKaldir")}
              </Button>
            )}
            {fotoHata && <div className="text-danger small mt-1">{fotoHata}</div>}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Kaydet ───────────────────────────────────────────────────────────────────

/**
 * Kaydet + fotoğraf. İki sayfanın servisleri farklı kabuk döndürdüğü için `api`, her biri
 * firmanın GÜNCEL LİSTESİNİ (dizi) döndüren fonksiyonlardır:
 *   { create(payload), update(id, payload), uploadFoto(id, file), deleteFoto(id) }
 *
 * Yeni kayıtta backend id'yi ayrıca vermiyor, yalnızca güncel listeyi döndürüyor; yeni satır
 * önceki listede olmayan id'dir. Fotoğraf adımı başarısız olursa kayıt YİNE de yapılmıştır —
 * bu yüzden fırlatmak yerine `fotoHatasi` ile döner, çağıran listeyi uygulayıp hatayı gösterir.
 *
 * Son yazmanın döndürdüğü liste ekrana basılır. PUT ve POST …/fotograf cevapları eskiden bayattı
 * ve burada ayrıca bir GET ile telafi ediliyordu; backend #255 ile düzeldi, telafi kaldırıldı.
 *
 * @returns {Promise<{ liste: Array, fotoHatasi: string|null }>}
 */
export async function yetkiliKaydet({ mode, id, payload, foto, oncekiListe = [], api }) {
  let liste = mode === "edit" ? await api.update(id, payload) : await api.create(payload);
  let satirId = id;
  if (mode !== "edit") {
    const eski = new Set((oncekiListe || []).map((k) => k.id));
    satirId = (liste || []).find((k) => !eski.has(k.id))?.id ?? null;
  }

  let fotoHatasi = null;
  if (satirId != null && (foto?.file || foto?.sil)) {
    try {
      liste = foto.file ? await api.uploadFoto(satirId, foto.file) : await api.deleteFoto(satirId);
    } catch (e) {
      fotoHatasi = i18n.t("components:yetkiliProfil.kayitYapildiFotografIslenemedi", { v: e.response?.data?.message || e.message });
    }
  }

  return { liste, fotoHatasi };
}
